import { supabase } from "@/integrations/supabase/client";

interface TipoEquipamento {
  id: string;
  nome: string;
  categoria: string;
  descricao?: string;
  createdAt: string;
  updatedAt: string;
}

interface Equipamento {
  id: string;
  tipo: string;
  identificacao?: string;
  condicao: string;
  fabricante?: string;
  modelo?: string;
  ano?: number;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

// Referência estática (tipos comuns de equipamentos de obra no Brasil).
const DEFAULT_TIPOS_EQUIPAMENTOS: TipoEquipamento[] = [
  { id: "def-1", nome: "Betoneira", categoria: "Misturador", descricao: "Mistura de concreto e argamassa", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-2", nome: "Furadeira", categoria: "Perfuração", descricao: "Perfuração em diversos materiais", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-3", nome: "Serra Circular", categoria: "Corte", descricao: "Corte de madeira e similares", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-4", nome: "Martelete", categoria: "Demolição", descricao: "Demolição e perfuração pesada", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-5", nome: "Guincho", categoria: "Elevação", descricao: "Elevação de materiais e cargas", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-6", nome: "Andaime", categoria: "Acesso", descricao: "Acesso a locais elevados", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-7", nome: "Vibrador de Concreto", categoria: "Adensamento", descricao: "Adensamento do concreto", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-8", nome: "Escavadeira", categoria: "Terraplanagem", descricao: "Escavação e movimentação de terra", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-9", nome: "Retroescavadeira", categoria: "Terraplanagem", descricao: "Escavação e carregamento", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
  { id: "def-10", nome: "Grua", categoria: "Elevação", descricao: "Elevação de cargas pesadas", createdAt: "2025-01-01T10:00:00.000Z", updatedAt: "2025-01-01T10:00:00.000Z" },
];

let _tipos: TipoEquipamento[] = [];
let _equipamentos: Equipamento[] = [];

function rowToTipo(row: any): TipoEquipamento {
  return { id: row.id, nome: row.nome, categoria: row.categoria ?? "", descricao: row.descricao ?? undefined, createdAt: row.created_at, updatedAt: row.updated_at };
}
function rowToEquip(row: any): Equipamento {
  return { id: row.id, tipo: row.tipo, identificacao: row.identificacao ?? undefined, condicao: row.condicao ?? "", fabricante: row.fabricante ?? undefined, modelo: row.modelo ?? undefined, ano: row.ano ?? undefined, ativo: row.ativo ?? true, createdAt: row.created_at, updatedAt: row.updated_at };
}
async function currentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

class EquipmentCache {
  static async hydrate(): Promise<void> {
    const [t, e] = await Promise.all([
      supabase.from("equipamento_tipos").select("*").order("created_at", { ascending: false }),
      supabase.from("equipamentos").select("*").order("created_at", { ascending: false }),
    ]);
    if (t.error) console.error("EquipmentCache.hydrate tipos:", t.error.message);
    else _tipos = (t.data ?? []).map(rowToTipo);
    if (e.error) console.error("EquipmentCache.hydrate equip:", e.error.message);
    else _equipamentos = (e.data ?? []).map(rowToEquip);
  }

  // Tipos
  static getTiposEquipamentos(): TipoEquipamento[] {
    return [..._tipos, ...DEFAULT_TIPOS_EQUIPAMENTOS];
  }
  static setTiposEquipamentos(tipos: TipoEquipamento[]): void {
    _tipos = tipos;
  }
  static addTipoEquipamento(tipo: TipoEquipamento): void {
    const now = new Date().toISOString();
    const temp: TipoEquipamento = { ...tipo, id: tipo.id || `tmp-${Date.now()}`, createdAt: tipo.createdAt || now, updatedAt: now };
    _tipos = [temp, ..._tipos];
    (async () => {
      const userId = await currentUserId();
      if (!userId) return;
      const { data, error } = await supabase.from("equipamento_tipos").insert({
        user_id: userId, nome: tipo.nome, categoria: tipo.categoria ?? null, descricao: tipo.descricao ?? null,
      }).select("*").single();
      if (error) { console.error("EquipmentCache.addTipo:", error.message); return; }
      _tipos = _tipos.map((x) => (x.id === temp.id ? rowToTipo(data) : x));
    })();
  }
  static updateTipoEquipamento(updated: TipoEquipamento): void {
    _tipos = _tipos.map((t) => (t.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : t));
    (async () => {
      const { error } = await supabase.from("equipamento_tipos").update({ nome: updated.nome, categoria: updated.categoria ?? null, descricao: updated.descricao ?? null }).eq("id", updated.id);
      if (error) console.error("EquipmentCache.updateTipo:", error.message);
    })();
  }
  static deleteTipoEquipamento(id: string): void {
    _tipos = _tipos.filter((t) => t.id !== id);
    (async () => { const { error } = await supabase.from("equipamento_tipos").delete().eq("id", id); if (error) console.error("EquipmentCache.deleteTipo:", error.message); })();
  }

  // Equipamentos
  static getEquipamentos(): Equipamento[] {
    return _equipamentos;
  }
  static setEquipamentos(equipamentos: Equipamento[]): void {
    _equipamentos = equipamentos;
  }
  static addEquipamento(equipamento: Equipamento): void {
    const now = new Date().toISOString();
    const temp: Equipamento = { ...equipamento, id: equipamento.id || `tmp-${Date.now()}`, createdAt: equipamento.createdAt || now, updatedAt: now };
    _equipamentos = [temp, ..._equipamentos];
    (async () => {
      const userId = await currentUserId();
      if (!userId) return;
      const { data, error } = await supabase.from("equipamentos").insert({
        user_id: userId, tipo: equipamento.tipo, identificacao: equipamento.identificacao ?? null, condicao: equipamento.condicao ?? null,
        fabricante: equipamento.fabricante ?? null, modelo: equipamento.modelo ?? null, ano: equipamento.ano ?? null, ativo: equipamento.ativo ?? true,
      }).select("*").single();
      if (error) { console.error("EquipmentCache.addEquip:", error.message); return; }
      _equipamentos = _equipamentos.map((x) => (x.id === temp.id ? rowToEquip(data) : x));
    })();
  }
  static updateEquipamento(updated: Equipamento): void {
    _equipamentos = _equipamentos.map((e) => (e.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : e));
    (async () => {
      const { error } = await supabase.from("equipamentos").update({
        tipo: updated.tipo, identificacao: updated.identificacao ?? null, condicao: updated.condicao ?? null,
        fabricante: updated.fabricante ?? null, modelo: updated.modelo ?? null, ano: updated.ano ?? null, ativo: updated.ativo ?? true,
      }).eq("id", updated.id);
      if (error) console.error("EquipmentCache.updateEquip:", error.message);
    })();
  }
  static deleteEquipamento(id: string): void {
    _equipamentos = _equipamentos.filter((e) => e.id !== id);
    (async () => { const { error } = await supabase.from("equipamentos").delete().eq("id", id); if (error) console.error("EquipmentCache.deleteEquip:", error.message); })();
  }

  static resetToDefaults(): void {
    _tipos = [];
    _equipamentos = [];
  }
}

export { EquipmentCache };
export type { TipoEquipamento, Equipamento };
